import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import './Switch.css';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Состояние «частично выбрано» (например «Выбрать все» при неполном выборе) */
  indeterminate?: boolean;
  id?: string;
  /** Видимая подпись рядом с тумблером */
  label?: ReactNode;
  /** Положение подписи относительно тумблера. По умолчанию справа. */
  labelPosition?: 'start' | 'end';
  /** Если подписи нет — задайте доступное имя для скринридеров */
  'aria-label'?: string;
  className?: string;
  name?: string;
  /** Клик по подписи/тумблеру (например stopPropagation в контекстном меню) */
  onClick?: MouseEventHandler<HTMLLabelElement>;
  style?: CSSProperties;
  /**
   * Не переводить фокус на input при клике (mousedown preventDefault).
   * Нужно во вложенных scroll-контейнерах: иначе браузер делает scrollIntoView и срывает вёрстку.
   */
  preventFocusScroll?: boolean;
}

export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  indeterminate = false,
  id: idProp,
  label,
  labelPosition = 'end',
  'aria-label': ariaLabel,
  className = '',
  name,
  onClick,
  style,
  preventFocusScroll = false,
}: SwitchProps) {
  const uid = useId();
  const inputId = idProp ?? `switch-${uid}`;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (el) el.indeterminate = indeterminate;
  }, [indeterminate]);

  const role = indeterminate ? 'checkbox' : 'switch';
  const showLabel = label != null && label !== '';
  const labelEl = showLabel ? <span className="switch__label">{label}</span> : null;

  return (
    <label
      className={`switch ${disabled ? 'switch_disabled' : ''} ${className}`.trim()}
      htmlFor={inputId}
      onClick={onClick}
      onMouseDown={
        preventFocusScroll && !disabled
          ? (e) => {
              e.preventDefault();
            }
          : undefined
      }
      style={style}
    >
      <input
        ref={inputRef}
        id={inputId}
        className="switch__input"
        type="checkbox"
        role={role}
        name={name}
        checked={checked}
        disabled={disabled}
        aria-label={showLabel ? undefined : ariaLabel}
        aria-checked={indeterminate ? 'mixed' : undefined}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      {labelPosition === 'start' ? labelEl : null}
      <span className="switch__track" aria-hidden="true" />
      {labelPosition === 'end' ? labelEl : null}
    </label>
  );
}
